/**
 * The API client. Cookies ride along (`credentials: "include"`), and every
 * failure is the C§1 envelope `{ ok: false, error, hint }`, thrown as ApiError.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly hint: string;
  /** The whole error body: some errors carry more, e.g. `409 conflict`'s current file. */
  readonly body: Record<string, unknown>;

  constructor(status: number, code: string, hint: string, body: Record<string, unknown> = {}) {
    super(hint);
    this.status = status;
    this.code = code;
    this.hint = hint;
    this.body = body;
  }
}

/**
 * The API's origin: https://api.hirecrew.app on crew-app (ADR-0013), set at
 * build as VITE_API_URL. Empty locally: the API is on the page's own origin.
 */
export const API_URL = ((import.meta.env.VITE_API_URL as string | undefined) ?? "").replace(
  /\/+$/,
  "",
);

export async function api<T = Record<string, unknown>>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
  /**
   * `timeoutMs`: give up on an unanswered request (an optimistic send's "Not
   * sent"). `signal`: abort it, e.g. when the session it started in ends.
   */
  opts: { timeoutMs?: number; signal?: AbortSignal } = {},
): Promise<T> {
  const signals = [opts.signal, opts.timeoutMs ? AbortSignal.timeout(opts.timeoutMs) : undefined];
  const live = signals.filter((s): s is AbortSignal => s !== undefined);
  const res = await fetch(`${API_URL}${path}`, {
    method,
    credentials: "include",
    signal: live.length > 1 ? AbortSignal.any(live) : live[0],
    headers: body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await res.json().catch(() => null)) as {
    ok?: boolean;
    error?: string;
    hint?: string;
    [more: string]: unknown;
  } | null;
  // An abort while the body was read is still an abort, never a null answer (Codex on #373).
  opts.signal?.throwIfAborted();
  if (!res.ok || data?.ok === false) {
    throw new ApiError(
      res.status,
      data?.error ?? "internal",
      data?.hint ?? "Something went wrong. Try again.",
      data ?? {},
    );
  }
  return data as T;
}

/** A file Crew kept from an upload (slice 5b spec §6.1). */
export type Uploaded = {
  id: string;
  name: string;
  mime: string;
  size: number;
  kind: "photo" | "file";
};

/**
 * `POST /api/files?name=` (slice 5b spec §6.1): the file as the raw body, no
 * multipart, so the Worker streams it into R2 with its length. Failures are
 * the same envelope as `api`'s.
 */
export async function upload(
  file: Blob & { name: string },
  signal?: AbortSignal,
): Promise<Uploaded> {
  const res = await fetch(`${API_URL}/api/files?name=${encodeURIComponent(file.name)}`, {
    method: "POST",
    credentials: "include",
    signal,
    headers: { "content-type": file.type || "application/octet-stream" },
    body: file,
  });
  const data = (await res.json().catch(() => null)) as {
    ok?: boolean;
    error?: string;
    hint?: string;
    file?: Uploaded;
  } | null;
  if (!res.ok || !data?.file) {
    throw new ApiError(
      res.status,
      data?.error ?? (res.status === 413 ? "too_large" : "internal"),
      data?.hint ??
        (res.status === 413 ? "Files are 20 MB at most." : "That file didn't upload. Try again."),
      data ?? {},
    );
  }
  return data.file;
}

/** The hint to show a person for any thrown error. */
export function errorHint(error: unknown): string {
  if (error instanceof ApiError) return error.hint;
  return "Couldn't reach Crew. Check your connection and try again.";
}
